/**
 * MiniProjectBomItemEditTray — full edit/create experience for a single
 * Mini-Project Resources (BOM) item. DEV-1110 Phase 2.
 *
 * Lifts the inline AddBomForm + EditBomForm out of BomSection into a
 * right-edge sliding tray that mirrors the GoalEditTray /
 * MiniProjectTaskEditTray pattern. Same fields, same template-lock
 * rules, same delete gating — just relocated for consistency.
 *
 * File naming uses "BomItem" to match the existing data-model-driven
 * file names in the codebase (`MiniProjectBomItemCard`, `BomSection`,
 * `MiniProjectBomItem`). User-facing strings say "Resources" per
 * POLICY_RESOURCE_VOCABULARY.md — the section was renamed from
 * Expenditures to Resources but the data model rename is tracked
 * separately.
 *
 * Mode-discriminated Props:
 *   - mode="create" : POST /api/mini-projects/<guid>/bom-items
 *   - mode="edit"   : PUT  /api/mini-projects/<guid>/bom-items/<bomItemGuid>
 *
 * Template-item semantics (preserved from EditBomForm):
 *   - isTemplateItem=true locks `name` (other fields stay editable)
 *   - Delete button is hidden on template items (per migration 170:
 *     soft-remove template items via procurementState='not_applicable')
 *   - Italic "template-locked" notice surfaces explanation to the Lead
 *
 * Field set (pure refactor — DEV-1110 scope locked 2026-05-26):
 *   - Create: name, quantity, unit, estimatedCost, purchaseLink
 *   - Edit:   name (locked if template), quantity, unit, estimatedCost,
 *             purchaseLink, notes
 *
 * No subsections — field count is low (5–6) so a flat column reads
 * cleaner than forcing Goal-tray-style section labels.
 *
 * Procurement-state pill stays on the card (MiniProjectBomItemCard
 * inline cycle) — not migrated into the tray. The card's pencil is
 * what opens this tray; the pill is its own quick-cycle affordance.
 */

import React, { useCallback, useState } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import {
  createMiniProjectBomItem,
  updateMiniProjectBomItem,
  deleteMiniProjectBomItem,
  type MiniProjectBomItem,
} from "../../api/miniProjects";
import { Tray } from "../Tray/Tray";
import styles from "./MiniProjectBomItemEditTray.module.css";

/* ── Types ─────────────────────────────────────────────────────── */

/**
 * Tray serves both create and edit flows. Mode-specific differences:
 *   - Title:  "Add resource" vs "Edit resource: <name>"
 *   - Notes:  edit-only field (matches pre-DEV-1110 behavior — create
 *             form historically lacked notes; preserving exactly to
 *             stay inside "pure refactor" scope)
 *   - Delete: edit-only, gated on !isTemplateItem
 *   - Save handler: createMiniProjectBomItem vs updateMiniProjectBomItem
 */
type Props =
  | {
      mode: "edit";
      open: boolean;
      item: MiniProjectBomItem | null;
      miniProjectGuid: string;
      onClose: () => void;
      onMutated: () => void;
    }
  | {
      mode: "create";
      open: boolean;
      miniProjectGuid: string;
      onClose: () => void;
      onMutated: () => void;
    };

/** Procurement-state cycle order — matches MiniProjectBomItemCard so
 *  the tray's quick-cycle is identical to the card's. */
const NEXT_STATE: Record<
  MiniProjectBomItem["procurementState"],
  MiniProjectBomItem["procurementState"]
> = {
  needed: "obtained",
  obtained: "not_applicable",
  not_applicable: "needed",
};

const STATE_LABEL_KEY: Record<MiniProjectBomItem["procurementState"], string> = {
  needed: "miniProject.cards.bom.state.needed",
  obtained: "miniProject.cards.bom.state.obtained",
  not_applicable: "miniProject.cards.bom.state.notApplicable",
};

/* ── Inline icons ──────────────────────────────────────────────── */

const TrashIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
  </svg>
);

/* ── Component ─────────────────────────────────────────────────── */

const MiniProjectBomItemEditTray: React.FC<Props> = (props) => {
  const { mode, open, onClose, onMutated, miniProjectGuid } = props;
  const { t } = useAppTranslation("pages");

  // Mode-discriminated value extraction. Edit derives initial state
  // from the item prop; create starts from blank defaults. The parent
  // keys this component on bomItemGuid (edit) or "create-<bool>"
  // (create) so useState initializers fire fresh on each open.
  const item = mode === "edit" ? props.item : null;
  const isTemplate = !!item?.isTemplateItem;

  // ── Local state ──
  const [name, setName] = useState<string>(item?.name ?? "");
  const [quantity, setQuantity] = useState<string>(
    item?.quantity != null ? String(item.quantity) : "",
  );
  const [unit, setUnit] = useState<string>(item?.unit ?? "");
  const [estimatedCost, setEstimatedCost] = useState<string>(
    item?.estimatedCost != null ? String(item.estimatedCost) : "",
  );
  const [purchaseLink, setPurchaseLink] = useState<string>(item?.purchaseLink ?? "");
  const [notes, setNotes] = useState<string>(item?.notes ?? "");

  // ── Actual-cost state (Migration 189, Skip 2026-05-28) ──
  // Three BOM fields for recording what was actually procured: actual
  // cost per unit, actual quantity received, purchase date. All optional
  // and now clearable — blanking a field on Save nulls it server-side via
  // the clearFields channel (DEV-1274), same as estimatedCost/quantity.
  const [actualCost, setActualCost] = useState<string>(
    item?.actualCost != null ? String(item.actualCost) : "",
  );
  const [actualQuantity, setActualQuantity] = useState<string>(
    item?.actualQuantity != null ? String(item.actualQuantity) : "",
  );
  const [purchaseDate, setPurchaseDate] = useState<string>(item?.purchaseDate ?? "");

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Save (branches on mode) ──
  const handleSave = useCallback(async () => {
    // Name validation: required for create, and for edit on non-template
    // items (template items lock the name field).
    const needsName = mode === "create" || !isTemplate;
    if (needsName && !name.trim()) {
      setError(t("miniProject.sections.error.nameRequired"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      // Skip 2026-05-28: recording actuals implies the resource has
      // been obtained. Auto-bump procurement state needed → obtained
      // when any actual field is set so the Budget rollups (counts,
      // resources list, top progress bar) all stay in sync without
      // the Lead having to remember to flip the pill separately.
      // Preserve 'not_applicable' (Lead may have a refund/return
      // scenario) and never downgrade 'obtained' back to 'needed' on
      // clear (lossy).
      const hasAnyActual = !!(actualCost || actualQuantity || purchaseDate);
      const currentState = item?.procurementState ?? "needed";
      const shouldAutoObtain = hasAnyActual && currentState === "needed";

      if (mode === "create") {
        await createMiniProjectBomItem(miniProjectGuid, {
          name: name.trim(),
          quantity: quantity ? Number(quantity) : undefined,
          unit: unit.trim() || undefined,
          estimatedCost: estimatedCost ? Number(estimatedCost) : undefined,
          purchaseLink: purchaseLink.trim() || undefined,
          // Migration 189 actuals — usually NULL at create time but
          // surfaced here so a Lead recording an item after-the-fact
          // can record planned and actual in one pass.
          actualCost: actualCost ? Number(actualCost) : undefined,
          actualQuantity: actualQuantity ? Number(actualQuantity) : undefined,
          purchaseDate: purchaseDate || undefined,
          // Auto-bump on create: if any actuals are entered up front,
          // start the item as 'obtained'. Otherwise the server defaults
          // to 'needed'.
          procurementState: shouldAutoObtain ? "obtained" : undefined,
        });
      } else if (item) {
        // Template items lock name. Server is the source of truth on
        // the lock; we skip sending name if locked so a tampered payload
        // still can't slip past. All other fields are editable on
        // template items (quantity, unit, cost, link, notes).
        // Full editor: every optional field shown is initialized from the
        // item, so a blank field means "clear it". Send a value when present;
        // list it in clearFields when blank. The server's HasValue convention
        // can't express a clear (null = "leave unchanged", which preserves
        // partial-patch callers like the procurement pill), so clearFields is
        // the explicit null-out channel. DEV-1274.
        const patch: Partial<MiniProjectBomItem> & { clearFields?: string[] } = {};
        const clearFields: string[] = [];

        if (quantity.trim()) patch.quantity = Number(quantity);
        else clearFields.push("quantity");

        if (unit.trim()) patch.unit = unit.trim();
        else clearFields.push("unit");

        if (estimatedCost.trim()) patch.estimatedCost = Number(estimatedCost);
        else clearFields.push("estimatedCost");

        if (purchaseLink.trim()) patch.purchaseLink = purchaseLink.trim();
        else clearFields.push("purchaseLink");

        if (notes.trim()) patch.notes = notes.trim();
        else clearFields.push("notes");

        if (actualCost.trim()) patch.actualCost = Number(actualCost);
        else clearFields.push("actualCost");

        if (actualQuantity.trim()) patch.actualQuantity = Number(actualQuantity);
        else clearFields.push("actualQuantity");

        if (purchaseDate) patch.purchaseDate = purchaseDate;
        else clearFields.push("purchaseDate");

        if (clearFields.length) patch.clearFields = clearFields;
        if (!isTemplate) {
          patch.name = name.trim();
        }
        // Never downgrade obtained → needed on clear (lossy); only auto-bump up.
        if (shouldAutoObtain) {
          patch.procurementState = "obtained";
        }
        await updateMiniProjectBomItem(miniProjectGuid, item.bomItemGuid, patch);
      }
      onMutated();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [
    mode,
    miniProjectGuid,
    item,
    isTemplate,
    name,
    quantity,
    unit,
    estimatedCost,
    purchaseLink,
    notes,
    actualCost,
    actualQuantity,
    purchaseDate,
    onMutated,
    t,
  ]);

  // ── Procurement-state cycle (edit-only; persists immediately via
  //    update endpoint, separate from the draft Save flow). The parent's
  //    onMutated then refetches and the item prop updates with the new
  //    state, so the pill re-renders without remounting the tray.
  const handleCycleState = useCallback(async () => {
    if (!item || busy) return;
    setBusy(true);
    setError(null);
    try {
      await updateMiniProjectBomItem(miniProjectGuid, item.bomItemGuid, {
        procurementState: NEXT_STATE[item.procurementState],
      });
      onMutated();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [item, busy, miniProjectGuid, onMutated]);

  // ── Delete (gated on edit + !template) ──
  const handleDelete = useCallback(async () => {
    if (!item || isTemplate) return;
    if (!window.confirm(t("miniProject.sections.bom.confirmDelete", { name: item.name }))) return;
    setBusy(true);
    setError(null);
    try {
      await deleteMiniProjectBomItem(miniProjectGuid, item.bomItemGuid);
      onMutated();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [item, isTemplate, miniProjectGuid, onMutated, t]);

  // ── Title ──
  // In edit mode the body is gated on item != null (the prior item
  // momentarily lingers while the Tray animates closed).
  const bodyOk = mode === "create" || item != null;
  const trayTitle =
    mode === "create"
      ? t("miniProject.cards.bom.createTrayTitle")
      : item
        ? t("miniProject.cards.bom.editTrayTitle", { name: item.name })
        : t("miniProject.cards.bom.createTrayTitle");

  return (
    <Tray open={open} onClose={onClose} title={trayTitle}>
      {bodyOk && (
        <>
          <Tray.Body className={styles.body}>
            {/* Procurement-state pill — edit mode only. Cycles state
                via the existing update endpoint; persists immediately
                (does not require Save). The card's pill is unreachable
                behind the tray scrim, so the tray needs its own. */}
            {mode === "edit" && item && (
              <div className={styles.row}>
                <label className={styles.label}>
                  {t("miniProject.cards.bom.stateLabel")}
                </label>
                <button
                  type="button"
                  className={`${styles.statePill} ${styles[`state_${item.procurementState}`]}`}
                  onClick={handleCycleState}
                  disabled={busy}
                  title={t("miniProject.cards.bom.markAs", {
                    state: t(STATE_LABEL_KEY[NEXT_STATE[item.procurementState]]),
                  })}
                >
                  {t(STATE_LABEL_KEY[item.procurementState])}
                </button>
              </div>
            )}

            <div className={styles.row}>
              <label className={styles.label}>
                {t("miniProject.sections.formLabel.name")}
              </label>
              <input
                className={styles.input}
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={busy || isTemplate}
                placeholder={
                  mode === "create"
                    ? t("miniProject.sections.bom.namePlaceholder")
                    : undefined
                }
              />
            </div>

            <div className={styles.row}>
              <label className={styles.label}>
                {t("miniProject.sections.formLabel.quantity")}
                <span className={styles.opt}>
                  {t("miniProject.sections.formLabel.optional")}
                </span>
              </label>
              <input
                className={styles.input}
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                disabled={busy}
              />
            </div>

            <div className={styles.row}>
              <label className={styles.label}>
                {t("miniProject.sections.formLabel.unit")}
                <span className={styles.opt}>
                  {t("miniProject.sections.formLabel.optional")}
                </span>
              </label>
              <input
                className={styles.input}
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
                disabled={busy}
                placeholder={t("miniProject.sections.bom.unitPlaceholder")}
              />
            </div>

            <div className={styles.row}>
              <label className={styles.label}>
                {t("miniProject.sections.formLabel.estCost")}
                <span className={styles.opt}>
                  {t("miniProject.sections.formLabel.optional")}
                </span>
              </label>
              <input
                className={styles.input}
                type="number"
                step="0.01"
                value={estimatedCost}
                onChange={(e) => setEstimatedCost(e.target.value)}
                disabled={busy}
              />
            </div>

            <div className={styles.row}>
              <label className={styles.label}>
                {t("miniProject.sections.formLabel.purchaseLink")}
                <span className={styles.opt}>
                  {t("miniProject.sections.formLabel.optional")}
                </span>
              </label>
              <input
                className={styles.input}
                value={purchaseLink}
                onChange={(e) => setPurchaseLink(e.target.value)}
                disabled={busy}
                placeholder={t("miniProject.sections.bom.linkPlaceholder")}
              />
            </div>

            {/* ── Actuals subsection (Migration 189, Skip 2026-05-28) ──
                Group label so the Lead can tell at a glance these three
                fields are about what actually happened, not what was
                planned. Locale keys carry defaultValue so the tray ships
                without a separate add-i18n-key.cjs run; canonical keys
                can be added later via the script. */}
            <div className={styles.row}>
              <label className={styles.label} style={{ fontWeight: 600 }}>
                {t("miniProject.sections.bom.actualSection", {
                  defaultValue: "Actuals (recorded after procurement)",
                })}
              </label>
            </div>

            <div className={styles.row}>
              <label className={styles.label}>
                {t("miniProject.sections.formLabel.actualCost", {
                  defaultValue: "Actual cost (per unit)",
                })}
                <span className={styles.opt}>
                  {t("miniProject.sections.formLabel.optional")}
                </span>
              </label>
              <input
                className={styles.input}
                type="number"
                step="0.01"
                value={actualCost}
                onChange={(e) => setActualCost(e.target.value)}
                disabled={busy}
              />
            </div>

            <div className={styles.row}>
              <label className={styles.label}>
                {t("miniProject.sections.formLabel.actualQuantity", {
                  defaultValue: "Actual quantity received",
                })}
                <span className={styles.opt}>
                  {t("miniProject.sections.formLabel.optional")}
                </span>
              </label>
              <input
                className={styles.input}
                type="number"
                step="0.01"
                value={actualQuantity}
                onChange={(e) => setActualQuantity(e.target.value)}
                disabled={busy}
                placeholder={t("miniProject.sections.bom.actualQuantityPlaceholder", {
                  defaultValue: "Defaults to planned quantity",
                })}
              />
            </div>

            <div className={styles.row}>
              <label className={styles.label}>
                {t("miniProject.sections.formLabel.purchaseDate", {
                  defaultValue: "Purchase date",
                })}
                <span className={styles.opt}>
                  {t("miniProject.sections.formLabel.optional")}
                </span>
              </label>
              <input
                className={styles.input}
                type="date"
                value={purchaseDate}
                onChange={(e) => setPurchaseDate(e.target.value)}
                disabled={busy}
              />
            </div>

            {/* Notes: edit-only (matches pre-DEV-1110 form set; create
                form never had a notes field). */}
            {mode === "edit" && (
              <div className={styles.row}>
                <label className={styles.label}>
                  {t("miniProject.sections.formLabel.notes")}
                  <span className={styles.opt}>
                    {t("miniProject.sections.formLabel.optional")}
                  </span>
                </label>
                <textarea
                  className={styles.textarea}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  disabled={busy}
                />
              </div>
            )}

            {isTemplate && (
              <div className={styles.lockedNote}>
                {t("miniProject.sections.bom.templateLocked")}
              </div>
            )}

            {error && <div className={styles.errorMsg}>{error}</div>}
          </Tray.Body>

          <Tray.Footer className={styles.footer}>
            {/* Delete: edit mode + non-template only. Template items
                are soft-removed via procurementState='not_applicable'
                (per migration 170). */}
            {mode === "edit" && !isTemplate && (
              <button
                type="button"
                className={`${styles.btn} ${styles.btnDanger}`}
                onClick={handleDelete}
                disabled={busy}
              >
                <TrashIcon /> {t("miniProject.sections.button.delete")}
              </button>
            )}
            <button
              type="button"
              className={styles.btn}
              onClick={onClose}
              disabled={busy}
              style={
                mode === "create" || isTemplate
                  ? { marginInlineStart: "auto" }
                  : undefined
              }
            >
              {t("miniProject.sections.button.cancel")}
            </button>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnPrimary}`}
              onClick={handleSave}
              disabled={busy}
            >
              {busy
                ? t("miniProject.sections.button.saving")
                : mode === "create"
                  ? t("miniProject.sections.bom.button.add")
                  : t("miniProject.sections.button.save")}
            </button>
          </Tray.Footer>
        </>
      )}
    </Tray>
  );
};

export default MiniProjectBomItemEditTray;
